import React, { useContext, useEffect, useRef, useState } from 'react';
import {
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	Grid,
	IconButton,
	ListItem,
	MenuItem,
	Select,
	Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import AppContext from '../../../Services/AppContext';
import PersistantData from '../../../Services/PersistantData';
import API_Service, { axiosPutConfig, axiosURIencode } from '../../../Services/API_Service';
import { req_feedback_submitted_status } from '../../../Helper/Constants';
import ImageViewer from '../../../Components/ImageViewer';

const STATUS_CONFIG = {
	'pending_resolve': { bg: 'rgba(230, 81, 0, 0.10)', color: '#ffb74d', label: 'feedback_status_pending_resolve' },
	'resolved': { bg: 'rgba(46, 125, 50, 0.12)', color: '#66bb6a', label: 'feedback_status_resolved' }
};

const STATUS_KEYS = ['pending_resolve', 'resolved'];

const STATUS_TAG_CLASS = {
	'pending_resolve': 'statusTagPendingResolve',
	'resolved': 'statusTagResolved'
};

const useStyles = makeStyles((theme) => ({
	dialog: {
		'& .MuiDialog-paper': {
			borderRadius: 12
		}
	},
	header: {
		padding: theme.spacing(2, 3),
		borderBottom: `1px solid ${theme.palette.divider}`,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between'
	},
	headerTitle: {
		fontWeight: 600,
		fontSize: 18
	},
	leftPanel: {
		maxHeight: '72vh',
		overflowY: 'auto',
		borderRight: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.default,
		padding: theme.spacing(1),
		'&::-webkit-scrollbar': {
			width: 6
		},
		'&::-webkit-scrollbar-track': {
			backgroundColor: 'transparent'
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: 'rgba(255,255,255,0.15)',
			borderRadius: 3,
			'&:hover': {
				backgroundColor: 'rgba(255,255,255,0.25)'
			}
		}
	},
	feedbackItem: {
		borderRadius: 8,
		marginBottom: theme.spacing(0.5),
		padding: theme.spacing(1.5),
		transition: 'all 0.15s ease',
		borderLeft: '3px solid transparent',
		'&:hover': {
			backgroundColor: theme.palette.action.hover
		}
	},
	feedbackItemSelected: {
		backgroundColor: theme.palette.background.paper,
		boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
		borderLeft: `3px solid ${theme.palette.primary.main}`,
		'&:hover': {
			backgroundColor: theme.palette.background.paper
		}
	},
	feedbackItemContent: {
		flex: 1,
		minWidth: 0
	},
	feedbackItemTitle: {
		fontWeight: 500,
		fontSize: 14,
		lineHeight: 1.3,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		color: theme.palette.text.primary
	},
	feedbackItemSub: {
		fontSize: 12,
		color: theme.palette.text.secondary,
		marginTop: 2,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap'
	},
	rightPanel: {
		padding: theme.spacing(3),
		maxHeight: '72vh',
		overflowY: 'auto',
		'&::-webkit-scrollbar': {
			width: 6
		},
		'&::-webkit-scrollbar-track': {
			backgroundColor: 'transparent'
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: 'rgba(255,255,255,0.15)',
			borderRadius: 3,
			'&:hover': {
				backgroundColor: 'rgba(255,255,255,0.25)'
			}
		}
	},
	sectionCard: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
		borderRadius: 8,
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper
	},
	sectionLabel: {
		fontSize: 11,
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(0.5)
	},
	detailTitle: {
		fontWeight: 600,
		fontSize: 20,
		marginBottom: theme.spacing(1)
	},
	statusTagRow: {
		display: 'flex',
		gap: theme.spacing(1)
	},
	statusTag: {
		cursor: 'pointer',
		fontWeight: 500,
		fontSize: 12,
		borderRadius: 6,
		height: 28,
		transition: 'all 0.15s ease'
	},
	statusTagUnselected: {
		backgroundColor: 'rgba(255,255,255,0.06)',
		color: 'rgba(255,255,255,0.35)',
		'&:hover': {
			backgroundColor: 'rgba(255,255,255,0.1)'
		}
	},
	statusTagPendingResolve: {
		backgroundColor: 'rgba(230, 81, 0, 0.10)',
		color: '#ffb74d'
	},
	statusTagResolved: {
		backgroundColor: 'rgba(46, 125, 50, 0.12)',
		color: '#66bb6a'
	},
	imageGrid: {
		marginTop: theme.spacing(1)
	},
	thumbnail: {
		width: 100,
		height: 100,
		objectFit: 'cover',
		cursor: 'pointer',
		borderRadius: 8,
		border: `1px solid ${theme.palette.divider}`,
		transition: 'transform 0.15s ease, box-shadow 0.15s ease',
		'&:hover': {
			transform: 'scale(1.05)',
			boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
		}
	},
	statusChip: {
		fontWeight: 500,
		fontSize: 11,
		borderRadius: 6,
		height: 22,
		marginLeft: theme.spacing(1)
	},
	metaRow: {
		display: 'flex',
		gap: theme.spacing(3),
		marginBottom: theme.spacing(2)
	},
	metaItem: {
		flex: 1
	},
	descriptionText: {
		fontSize: 14,
		lineHeight: 1.6,
		whiteSpace: 'pre-wrap',
		color: theme.palette.text.primary
	},
	noDataText: {
		padding: theme.spacing(4),
		textAlign: 'center'
	},
	confirmDialog: {
		zIndex: '1400 !important',
		'& .MuiDialog-paper': {
			borderRadius: 12,
			minWidth: 320
		}
	},
	confirmContent: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: theme.spacing(3, 4, 1),
		textAlign: 'center'
	},
	confirmText: {
		fontSize: 15,
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(1.5)
	},
	confirmChip: {
		fontWeight: 600,
		fontSize: 14,
		borderRadius: 8,
		height: 32,
		marginBottom: theme.spacing(1)
	},
	confirmActions: {
		justifyContent: 'center',
		padding: theme.spacing(1, 3, 2.5),
		gap: theme.spacing(1.5)
	},
	confirmBtnCancel: {
		borderRadius: 8,
		textTransform: 'none',
		minWidth: 90
	},
	confirmBtnConfirm: {
		borderRadius: 8,
		textTransform: 'none',
		minWidth: 90,
		boxShadow: 'none'
	},
	undoBtn: {
		color: '#FEF200',
		fontWeight: 600,
		textTransform: 'none'
	},
	statusDropdown: {
		fontSize: 13,
		fontWeight: 500,
		borderRadius: 6,
		width: '100%',
		transition: 'border-color 0.2s ease',
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: 'rgba(255,255,255,0.15)',
			transition: 'border-color 0.2s ease'
		},
		'&:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: 'rgba(255,255,255,0.3)'
		},
		'& .MuiSelect-root': {
			padding: '6px 32px 6px 10px',
			display: 'flex',
			alignItems: 'center',
			gap: 6,
			minHeight: 'unset'
		}
	},
	statusDot: {
		width: 8,
		height: 8,
		borderRadius: '50%',
		display: 'inline-block',
		flexShrink: 0
	},
	statusMenuDot: {
		width: 8,
		height: 8,
		borderRadius: '50%',
		display: 'inline-block',
		flexShrink: 0,
		marginRight: 8
	}
}));

const getImages = (imageListStr) => {
	try {
		const parsed = JSON.parse(imageListStr);
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
};

export default function FeedbackDetailDialog({
	open,
	onClose,
	feedbackList,
	selectedIndex,
	onStatusUpdated
}) {
	const classes = useStyles();
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const singleton = PersistantData.getInstance();
	const [gState] = useContext(AppContext);
	var { translation: language, client } = gState;
	if (typeof (language) === 'undefined') {
		language = singleton.getlang();
	}

	const [currentIndex, setCurrentIndex] = useState(selectedIndex);
	const [imageViewerOpen, setImageViewerOpen] = useState(false);
	const [selectedImage, setSelectedImage] = useState('');
	const [confirmStatus, setConfirmStatus] = useState(null);
	const [pendingStatus, setPendingStatus] = useState(null);
	const itemRefs = useRef([]);

	useEffect(() => {
		setCurrentIndex(selectedIndex);
	}, [selectedIndex, open]);

	useEffect(() => {
		setPendingStatus(null);
		setConfirmStatus(null);
		if (itemRefs.current[currentIndex]) {
			itemRefs.current[currentIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	}, [currentIndex]);

	const getStatusLabel = (status) => {
		const config = STATUS_CONFIG[status];
		return config ? language[config.label] : status;
	};

	const handleUndoStatus = (itemId, revertTo, revertFrom) => {
		API_Service.put(
			req_feedback_submitted_status,
			axiosURIencode({ id: itemId, status: revertTo }),
			axiosPutConfig(client)
		).then(({ data }) => {
			if (parseInt(data.result) === 1) {
				onStatusUpdated(revertFrom, revertTo);
			} else {
				enqueueSnackbar(data.message || 'Error', { variant: 'error' });
			}
		}).catch(() => enqueueSnackbar('Network error', { variant: 'error' }));
	};

	const handleConfirmUpdate = () => {
		const newStatus = confirmStatus;
		setConfirmStatus(null);

		const item = feedbackList[currentIndex];
		if (!item || item.status === newStatus) return;

		const oldStatus = item.status;
		const itemId = item.id;

		API_Service.put(
			req_feedback_submitted_status,
			axiosURIencode({ id: itemId, status: newStatus }),
			axiosPutConfig(client)
		)
		.then(({ data }) => {
			if (parseInt(data.result) === 1) {
				setPendingStatus(newStatus);
				setTimeout(() => {
					setPendingStatus(null);
					onStatusUpdated(oldStatus, newStatus);
					enqueueSnackbar(
						`${language.feedback_status_updated} ${getStatusLabel(newStatus)}`,
						{
							variant: 'success',
							autoHideDuration: 5000,
							action: (snackbarId) => (
								<Button
									size="small"
									className={classes.undoBtn}
									onClick={() => {
										closeSnackbar(snackbarId);
										handleUndoStatus(itemId, oldStatus, newStatus);
									}}
								>
									{language.undo}
								</Button>
							)
						}
					);
				}, 1000);
			} else {
				enqueueSnackbar(data.message || 'Error', { variant: 'error' });
			}
		})
		.catch(() => enqueueSnackbar('Network error', { variant: 'error' }));
	};

	const currentItem = feedbackList && feedbackList[currentIndex];
	const images = currentItem ? getImages(currentItem.image_list) : [];
	const displayStatus = pendingStatus || (currentItem && currentItem.status);

	return (
		<>
			<Dialog maxWidth="lg" fullWidth open={open} onClose={onClose} className={classes.dialog}>
				<div className={classes.header}>
					<Typography className={classes.headerTitle}>
						{language.feedback_detail}
					</Typography>
					<IconButton size="small" onClick={onClose}>
						<CloseIcon />
					</IconButton>
				</div>
				<DialogContent dividers>
					<Grid container>
						<Grid item xs={4} className={classes.leftPanel}>
							{feedbackList && feedbackList.length > 0 ? (
								feedbackList.map((item, idx) => (
									<ListItem
										key={idx}
										button
										disableRipple
										ref={(el) => { itemRefs.current[idx] = el; }}
										className={`${classes.feedbackItem} ${idx === currentIndex ? classes.feedbackItemSelected : ''}`}
										onClick={() => setCurrentIndex(idx)}
									>
										<div className={classes.feedbackItemContent}>
											<Typography className={classes.feedbackItemTitle}>
												{item.feedback_title_name}
											</Typography>
											<Typography className={classes.feedbackItemSub}>
												{item.cust_company_name}
											</Typography>
										</div>
										<Chip
											size="small"
											label={getStatusLabel(item.status)}
											className={`${classes.statusChip} ${classes[STATUS_TAG_CLASS[item.status]] || ''}`}
										/>
									</ListItem>
								))
							) : (
								<Typography variant="body2" color="textSecondary" className={classes.noDataText}>
									{language.no_data}
								</Typography>
							)}
						</Grid>
						<Grid item xs={8} className={classes.rightPanel}>
							{currentItem ? (
								<>
									<Typography className={classes.detailTitle}>
										{currentItem.feedback_title_name}
									</Typography>

									<div className={classes.metaRow}>
										<div className={classes.metaItem}>
											<Typography className={classes.sectionLabel}>
												{language.customer}
											</Typography>
											<Typography variant="body1">
												{currentItem.cust_company_name}
											</Typography>
										</div>
										<div className={classes.metaItem}>
											<Typography className={classes.sectionLabel}>
												{language.brand_code}
											</Typography>
											<Typography variant="body1">
												{currentItem.brand_code || '-'}
											</Typography>
										</div>
										<div className={classes.metaItem}>
											<Typography className={classes.sectionLabel}>
												{language.status}
											</Typography>
											<Select
												value={displayStatus || ''}
												disabled={!!pendingStatus}
												variant="outlined"
												className={classes.statusDropdown}
												style={displayStatus && STATUS_CONFIG[displayStatus] ? {
													color: STATUS_CONFIG[displayStatus].color
												} : {}}
												renderValue={(val) => {
													const cfg = STATUS_CONFIG[val];
													if (!cfg) return val;
													return (
														<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
															<span className={classes.statusDot} style={{ backgroundColor: cfg.color }} />
															{language[cfg.label]}
														</span>
													);
												}}
												onChange={(e) => {
													const newKey = e.target.value;
													if (newKey !== displayStatus && !pendingStatus) {
														setConfirmStatus(newKey);
													}
												}}
											>
												{STATUS_KEYS.map((statusKey) => {
													const config = STATUS_CONFIG[statusKey];
													return (
														<MenuItem key={statusKey} value={statusKey} style={{ color: config.color, fontSize: 13 }}>
															<span className={classes.statusMenuDot} style={{ backgroundColor: config.color }} />
															{language[config.label]}
														</MenuItem>
													);
												})}
											</Select>
										</div>
									</div>

									<div className={classes.sectionCard}>
										<Typography className={classes.sectionLabel}>
											{language.feedback_description}
										</Typography>
										<Typography className={classes.descriptionText}>
											{currentItem.description}
										</Typography>
									</div>

									{images.length > 0 && (
										<div className={classes.sectionCard}>
											<Typography className={classes.sectionLabel}>
												{language.feedback_images}
											</Typography>
											<Grid container spacing={1} className={classes.imageGrid}>
												{images.map((img, idx) => (
													<Grid item key={idx}>
														<img
															src={img}
															alt={`feedback-img-${idx}`}
															className={classes.thumbnail}
															onClick={() => {
																setSelectedImage(img);
																setImageViewerOpen(true);
															}}
														/>
													</Grid>
												))}
											</Grid>
										</div>
									)}

									<div className={classes.metaRow}>
										<div className={classes.metaItem}>
											<Typography className={classes.sectionLabel}>
												{language.salesman}
											</Typography>
											<Typography variant="body1">
												{currentItem.created_by_name}
											</Typography>
										</div>
										<div className={classes.metaItem}>
											<Typography className={classes.sectionLabel}>
												{language.date}
											</Typography>
											<Typography variant="body1">
												{moment(currentItem.created_at, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY HH:mm')}
											</Typography>
										</div>
									</div>

								</>
							) : (
								<Typography variant="body2" color="textSecondary" className={classes.noDataText}>
									{language.no_data}
								</Typography>
							)}
						</Grid>
					</Grid>
				</DialogContent>
			</Dialog>

			<Dialog
				open={confirmStatus !== null}
				onClose={() => setConfirmStatus(null)}
				maxWidth="xs"
				className={classes.confirmDialog}
			>
				<div className={classes.confirmContent}>
					<Typography className={classes.confirmText}>
						{language.feedback_confirm_status}
					</Typography>
					{confirmStatus && (
						<Chip
							label={getStatusLabel(confirmStatus)}
							className={`${classes.confirmChip} ${classes[STATUS_TAG_CLASS[confirmStatus]] || ''}`}
						/>
					)}
				</div>
				<DialogActions className={classes.confirmActions}>
					<Button
						className={classes.confirmBtnCancel}
						variant="outlined"
						onClick={() => setConfirmStatus(null)}
					>
						{language.cancel}
					</Button>
					<Button
						className={classes.confirmBtnConfirm}
						color="primary"
						variant="contained"
						onClick={handleConfirmUpdate}
					>
						{language.confirm}
					</Button>
				</DialogActions>
			</Dialog>

			<ImageViewer
				open={imageViewerOpen}
				onDismiss={() => setImageViewerOpen(false)}
				src={selectedImage}
			/>
		</>
	);
}
